import React, { Component, Suspense } from "react"

/* Socket IO */
import { io } from "socket.io-client"

/* Constants */
import { env, utils, tempstake } from "./constants"
import { LOADING, ERROR, WAITING, CHOICE, DEALING, INSURANCE, EVENMONEY, GAME, DEALER, RESULT, CLOSED } from "./constants/status"

/* UUID */
import { v4 as uuidv4 } from "uuid"

/* Widgets */
import { Preloader, Chips, Bets, Decision, Hand, Result, History } from "./widgets"

/* Components */
import { GamePayout, UvpTimer } from "./components"

/* Rules */
import { list } from "./constants/rules"

/* POKER UI (shared chrome) */
import { Background, Error } from "@cuba-go/poker-ui"

const GameControl = React.lazy(() => import("@cuba-go/poker-ui").then(mode => ({ default: mode.GameControl })))
const Balance = React.lazy(() => import("@cuba-go/poker-ui").then(mode => ({ default: mode.Balance })))
const Internet = React.lazy(() => import("@cuba-go/poker-ui").then(mode => ({ default: mode.Internet })))
const GameInfo = React.lazy(() => import("@cuba-go/poker-ui").then(mode => ({ default: mode.GameInfo })))
const Notification = React.lazy(() => import("@cuba-go/poker-ui").then(mode => ({ default: mode.Notification })))
const FloatingMessages = React.lazy(() => import("@cuba-go/poker-ui").then(mode => ({ default: mode.FloatingMessages })))

/* Fields */
const pathname = window.location.search
const token = pathname.length > 1 ? pathname.substring(1) : ""


/* Entry Point */
class App extends Component {

    constructor() {
        super()

        this.state = {

            /* General */
            status: LOADING,
            balance: null,
            timer: 0,

            /* Bets */
            stake: { ...tempstake },
            last: null,
            list: [],
            selected: 0,
            lastWin: 0,
            /* Committed stake from the server — reflects double / split.
               main = BET-spot chip amount, total = "общая ставка" indicator */
            stakeInfo: { main: 0, total: 0 },

            /* Shared lanes */
            lanes: { main: [], s1: null, s2: null },
            laneScores: { main: 0, s1: 0, s2: 0 },
            dealerCards: [],
            dealerScore: 0,
            activeLane: "main",

            /* My participation */
            iPlay: false,
            split: false,
            lockedLanes: {},

            /* Decisions */
            turn: null,
            stats: { counts: {}, total: 0 },
            myChoice: null,

            /* Insurance */
            insuranceChoice: null,
            insuranceResult: null,
            insurance: 0,
            insuranceOn: false,

            /* Even money (player blackjack vs dealer Ace) */
            evenMoneyChoice: null,

            /* Whether the main bet was doubled (for the on-screen ante chip) */
            doubleMain: false,

            /* Side bets & result */
            sideResults: null,
            result: null,

            /* Info */
            dealerInfo: null,
            gameInfo: null,
            transactions: [],
            operations: [],
            history: [],

            /* Session */
            closed: false,
            newDevice: false,
            isDisconnected: false,
            isPaused: false,
        }

        this.socket = io(env.endpoint, { auth: { token: `${token}`, type: "player" } })
    }

    componentDidMount = () => {
        this.game()
    }

    /* ============================================================ */
    /* SOCKET EVENTS                                                */
    /* ============================================================ */
    game = () => {

        this.socket.on("connect_error", () => {
            this.clear()
            const timer = setTimeout(() => { this.setState({ status: ERROR }); clearTimeout(timer) }, 1000)
        })

        this.socket.on("newDeviceConnection", () => {
            this.setState({ newDevice: true })
            this.socket.disconnect()
            this.clear()
        })

        this.socket.on("disconnect", () => {
            this.setState({ isDisconnected: true })
            this.clear()
        })

        this.socket.on("connect", () => {
            /* Only leave the preloader if no snapshot/status arrives; never overwrite a received status */
            const timer = setTimeout(() => {
                if (this.state.status === LOADING) this.setState({ status: WAITING })
                clearTimeout(timer)
            }, 2500)
        })

        this.socket.on("balance", balance => this.setState({ balance }))
        this.socket.on("gameInfo", gameInfo => this.setState({ gameInfo }))
        this.socket.on("timer", timer => this.setState({ timer }))
        this.socket.on("sideResults", sideResults => this.setState({ sideResults }))

        /* Status transitions */
        this.socket.on("status", status => {
            const previous = this.state.status
            if (previous === status) return

            this.setState({ status }, () => {
                if (status === CHOICE) this.clear()
                if (status === DEALING) this.setState({ iPlay: (parseFloat(this.state.stake.main) || 0) > 0 })
            })
        })

        /* Card dealt to a lane or the dealer */
        this.socket.on("card", data => this.addCard(data))

        /* Dealer reveals the hole card */
        this.socket.on("reveal", ({ card, score }) => this.revealHole(card, score))

        /* Decision window opens */
        this.socket.on("turn", turn => {
            this.setState({ turn, activeLane: turn.lane, myChoice: null, timer: turn.timer })
        })

        /* Active hand changed but no decision yet (a split hand about to receive
           its card). Highlight that single hand and hide the stale decision
           panel until its real "turn" arrives — so only one hand blinks. */
        this.socket.on("activeLane", ({ lane }) => {
            this.setState({ activeLane: lane, turn: null, myChoice: null })
        })

        /* Committed stake update (double / split) — drives the BET chip and the
           "общая ставка" indicator so they match the money actually wagered. */
        this.socket.on("stakeInfo", info => this.setState({ stakeInfo: info || { main: 0, total: 0 } }))

        /* Live decision statistics */
        this.socket.on("stats", stats => this.setState({ stats }))

        /* Hand split (broadcast — render the two lanes) */
        this.socket.on("split", ({ s1, s2 }) => {
            const lanes = { ...this.state.lanes, s1: s1 || [], s2: s2 || [] }
            const laneScores = { ...this.state.laneScores, s1: this.score(s1), s2: this.score(s2) }
            this.setState({ lanes, laneScores })
        })

        /* You are one of the splitters */
        this.socket.on("youSplit", () => {
            this.setState({ split: true })
        })

        /* One of your hands is locked (individual freeze) */
        this.socket.on("lock", data => this.lockLane(data))

        /* Personal round result */
        this.socket.on("result", result => {
            this.setState({ result })
            if (result && parseFloat(result.sum) > 0) this.setState({ lastWin: parseFloat(result.sum) })
        })

        this.socket.on("insuranceResult", insuranceResult => {
            this.setState({ insuranceResult })
            /* Insurance lost -> drop the +½ from the BET chip so it returns to the
               main bet (e.g. 300 -> 200); the insurance stake is gone. */
            if (insuranceResult && insuranceResult.text === "lose") this.setState({ insuranceOn: false })
        })

        this.socket.on("history", history => this.setState({ history }))

        /* Reconnection snapshot */
        this.socket.on("reconnection", data => this.reconnection(data))

        this.socket.on("transaction", data => {
            if (data && data.id) {
                const operations = [...this.state.operations, data]
                this.setState({ operations, transactions: [...this.state.transactions, data] })
                const timer = setTimeout(() => {
                    this.setState({ operations: this.state.operations.filter(item => item.id !== data.id) })
                    clearTimeout(timer)
                }, 3500)
            }
        })

        this.socket.on("editTransaction", data => {
            const transactions = [...this.state.transactions]
            const index = transactions.findIndex(e => e.number === data.number)
            if (index > -1) { transactions[index].status = data.status; this.setState({ transactions }) }
        })

        this.socket.on("pause", () => { this.setState({ isPaused: true }); this.notify("pause", "show") })
        this.socket.on("continue", () => { this.setState({ isPaused: false }); this.notify("pause", "close") })

        this.socket.on("forceEnd", () => this.clear())
    }

    /* ============================================================ */
    /* CARD / HAND HANDLING                                         */
    /* ============================================================ */
    /* Blackjack total (face cards = 10, Ace = 1/11) from card.value 2..14 */
    score = (cards = []) => {
        if (!cards || !cards.length) return 0
        let total = 0
        let aces = 0
        cards.forEach((card) => {
            const value = parseInt(card.value)
            if (value === 14) { aces = aces + 1; total = total + 11 }
            else if (value >= 11) total = total + 10
            else total = total + value
        })
        while (total > 21 && aces > 0) { total = total - 10; aces = aces - 1 }
        return total
    }

    addCard = ({ target, lane, card, score, hidden }) => {

        /* Dealer */
        if (target === "dealer") {
            const dealerCards = [...this.state.dealerCards]
            if (dealerCards.findIndex(c => c.uid === card.uid) === -1) {
                dealerCards.push(hidden ? { ...card, hidden: true } : card)
            }
            this.setState({ dealerCards, dealerScore: score })
            return
        }

        /* Player lane — frozen once this player locked it */
        if (this.state.lockedLanes[lane]) return

        const key = lane || "main"
        const lanes = { ...this.state.lanes }
        const laneScores = { ...this.state.laneScores }
        const current = lanes[key] ? [...lanes[key]] : []

        if (current.findIndex(c => c.uid === card.uid) === -1) {
            current.push(card)
        }

        lanes[key] = current
        laneScores[key] = score
        this.setState({ lanes, laneScores })
    }

    revealHole = (card, score) => {
        const dealerCards = this.state.dealerCards.map(c => (c.uid === card.uid || c.hidden) ? card : c)
        this.setState({ dealerCards, dealerScore: score })
    }

    lockLane = ({ lane, score, cards, blackjack, charlie, bust, surrendered }) => {
        const lockedLanes = { ...this.state.lockedLanes, [lane]: { score, blackjack, charlie, bust, surrendered } }
        const lanes = { ...this.state.lanes, [lane]: cards || this.state.lanes[lane] }
        const laneScores = { ...this.state.laneScores, [lane]: score }
        this.setState({ lockedLanes, lanes, laneScores })
    }

    /* ============================================================ */
    /* ACTIONS                                                      */
    /* ============================================================ */
    setStake = stake => {
        this.setState({ stake, last: stake, lastWin: 0 })
        this.socket.emit("stake", stake)
    }

    decision = action => {
        /* Doubling the main hand doubles the on-screen ante chip */
        if (action === "double" && this.state.activeLane === "main") this.setState({ doubleMain: true })
        this.setState({ myChoice: action })
        this.socket.emit("decision", action)
    }

    insurance = answer => {
        /* Taking insurance adds half the ante to the on-screen ante chip */
        this.setState({ insuranceChoice: answer, insuranceOn: answer === "yes" })
        this.socket.emit("insurance", answer)
    }

    /* Even money (player blackjack vs dealer Ace): "yes" -> paid 1:1 */
    evenMoney = answer => {
        this.setState({ evenMoneyChoice: answer })
        this.socket.emit("evenMoney", answer)
    }

    /* Whether this player may act on a lane right now */
    canAct = lane => {
        const { iPlay, split, lockedLanes } = this.state
        if (!iPlay || lockedLanes[lane]) return false
        return lane === "main" ? !split : split
    }

    clear = () => {
        this.setState({
            stake: { ...tempstake },
            list: [],
            lanes: { main: [], s1: null, s2: null },
            laneScores: { main: 0, s1: 0, s2: 0 },
            dealerCards: [],
            dealerScore: 0,
            activeLane: "main",
            iPlay: false,
            split: false,
            lockedLanes: {},
            turn: null,
            stats: { counts: {}, total: 0 },
            myChoice: null,
            insuranceChoice: null,
            insuranceResult: null,
            insurance: 0,
            insuranceOn: false,
            evenMoneyChoice: null,
            doubleMain: false,
            stakeInfo: { main: 0, total: 0 },
            sideResults: null,
            result: null,
            transactions: [],
            operations: [],
        })
    }

    reconnection = data => {

        if (data.reason === "transaction") this.notify("transaction", "show")
        if (data.isPaused) this.notify("pause", "show")

        const timer = setTimeout(() => { this.notify("transaction", "close"); clearTimeout(timer) }, 3000)

        if (data.balance) this.setState({ balance: data.balance })

        const positions = data.positions || []
        const split = positions.some(p => p.lane === "s1" || p.lane === "s2")
        const doubleMain = positions.some(p => p.lane === "main" && p.doubled)
        const lockedLanes = {}
        positions.forEach(p => { if (p.locked) lockedLanes[p.lane] = { score: p.score, blackjack: p.blackjack, charlie: p.charlie, bust: p.score > 21 } })

        const lanes = {
            main: (data.lanes && data.lanes.main) || [],
            s1: data.lanes ? data.lanes.s1 : null,
            s2: data.lanes ? data.lanes.s2 : null,
        }

        this.setState({
            status: data.status,
            timer: data.timer || 0,
            stake: data.bets || { ...tempstake },
            last: data.bets || null,
            iPlay: positions.length > 0,
            split,
            lockedLanes,
            lanes,
            laneScores: {
                main: this.score(lanes.main),
                s1: lanes.s1 ? this.score(lanes.s1) : 0,
                s2: lanes.s2 ? this.score(lanes.s2) : 0,
            },
            dealerCards: data.dealerCards || [],
            dealerScore: data.dealerScore || 0,
            activeLane: data.activeLane || "main",
            stakeInfo: data.stakeInfo || { main: 0, total: 0 },
            sideResults: data.sideResults || null,
            insurance: data.insurance || 0,
            insuranceOn: (data.insurance || 0) > 0,
            evenMoneyChoice: data.evenMoneyChoice || null,
            doubleMain,
            turn: data.turn || null,
            myChoice: null,
            result: data.result || null,
            history: data.history || [],
            dealerInfo: data.dealerInfo || null,
            gameInfo: data.gameInfo || null,
            transactions: data.transactions || [],
            isPaused: data.isPaused,
        })
    }

    /* ============================================================ */
    /* NOTIFICATIONS / MESSAGING                                    */
    /* ============================================================ */
    notify = (type, action) => {
        const run = () => {
            if (!this._notify) return
            if (action === "show") this._notify.show(type)
            else if (action === "close") this._notify.close(type)
        }
        if (this._notify) run()
        else setTimeout(run, 2000)
    }

    checkInternetSpeed = speed => {
        if (parseInt(speed) < 5) this.notify("internet", "show")
        else this.notify("internet", "close")
    }

    sendMessage = message => {
        this.socket.emit("messageFromPlayer", message.message)
        if (this._floatingMessages) this._floatingMessages.sendMessage(message)
    }

    sendSupportMessage = message => this.socket.emit("supportMessage", message)

    quitGame = () => this.socket.emit("gameAction", "quit")

    /* ============================================================ */
    /* RENDER HELPERS                                               */
    /* ============================================================ */
    _timer = () => {
        const { timer, status } = this.state
        return <UvpTimer status={status} timer={timer} />
    }

    _dealer = () => {
        const { dealerCards, dealerScore } = this.state
        const revealed = dealerCards.length > 0 && dealerCards.every(c => !c.hidden)
        const blackjack = revealed && dealerCards.length === 2 && dealerScore === 21
        const bust = revealed && dealerScore > 21
        return <Hand variant="dealer" cards={dealerCards} score={dealerScore} label="ДИЛЕР" blackjack={blackjack} bust={bust} slots={6} />
    }

    _lanesToShow = () => {
        /* Only ever show my own situation, never another player's hands:
           - I split       -> only my two split hands (Рука 1 / Рука 2), no main.
           - otherwise      -> only the main hand (covers non-splitters AND
             spectators, live and after a reconnection).
           `split` is true only for the actual splitter (youSplit / positions),
           so a spectator or non-splitter always falls through to ["main"]. */
        return this.state.split ? ["s1", "s2"] : ["main"]
    }

    _laneBadge = lane => {
        const locked = this.state.lockedLanes[lane]
        if (!locked) return null
        if (locked.blackjack) return "bj"
        if (locked.bust) return "lose"
        if (locked.charlie) return "win"
        return null
    }

    _player = () => {

        const { lanes, laneScores, activeLane, status, myChoice } = this.state
        const show = this._lanesToShow()

        return (
            <div className={`bj-player bj-player-${show.length}`}>
                {show.map(lane => {
                    const cards = lanes[lane] || []
                    const active = status === GAME && activeLane === lane && this.canAct(lane)
                    const locked = this.state.lockedLanes[lane]

                    /* Optimistic mark: show stand / surrender the moment the
                       player picks it, without waiting for the server lock */
                    const myPick = active ? myChoice : null

                    const surrendered = !!(locked && locked.surrendered) || myPick === "surrender"
                    /* Blackjack badge only for a NATURAL blackjack — the opening
                       two-card 21 on the main hand. A 21 assembled on a split
                       hand (e.g. A+10 after splitting aces) is just 21, not a
                       blackjack, so it must not show the BJ crown. */
                    const blackjack = (locked && locked.blackjack) || (lane === "main" && cards.length === 2 && laneScores[lane] === 21)
                    const bust = (locked && locked.bust) || laneScores[lane] > 21
                    const stand = (!!locked && !locked.blackjack && !locked.bust && !locked.charlie) || myPick === "stand"
                    return (
                        <Hand
                            key={lane}
                            variant="player"
                            cards={cards}
                            score={laneScores[lane]}
                            badge={this._laneBadge(lane)}
                            active={active}
                            arrow={active && show.length > 1}
                            blackjack={blackjack}
                            stand={stand}
                            bust={bust}
                            surrendered={surrendered}
                            label={show.length > 1 ? (lane === "s1" ? "РУКА 1" : lane === "s2" ? "РУКА 2" : "MAIN") : "РУКА ИГРОКА"}
                            slots={lane === "main" ? 6 : 5}
                        />
                    )
                })}
            </div>
        )
    }

    _controls = () => {

        const { status, turn, stats, myChoice, activeLane, insuranceChoice, evenMoneyChoice, result } = this.state

        if (result) return null

        /* Insurance offer (normal hand vs dealer Ace) */
        if (status === INSURANCE && this.state.iPlay) {
            return (
                <Decision
                    mode="insurance"
                    insuranceChoice={insuranceChoice}
                    onInsurance={answer => this.insurance(answer)}
                />
            )
        }

        /* Even money offer (player blackjack vs dealer Ace) */
        if (status === EVENMONEY && this.state.iPlay) {
            return (
                <Decision
                    mode="evenmoney"
                    evenMoneyChoice={evenMoneyChoice}
                    onEvenMoney={answer => this.evenMoney(answer)}
                />
            )
        }

        /* Decision window */
        if (status === GAME && turn && this.canAct(turn.lane) && activeLane === turn.lane) {
            return (
                <Decision
                    mode="decision"
                    actions={turn.actions}
                    stats={stats}
                    myChoice={myChoice}
                    canAct={true}
                    onDecision={action => this.decision(action)}
                />
            )
        }

        return null
    }

    _bets = () => {

        const { status, stake, selected, list, balance, result, sideResults, doubleMain, insuranceOn, insurance, stakeInfo } = this.state
        const { chips, max } = utils.getData(token)

        /* Insurance pays 2:1 — show 2 x insurance as the winning chip */
        const insuranceWin = result && result.sideResults && result.sideResults.insurance && result.sideResults.insurance.text === "win"
            ? parseFloat(insurance) * 2
            : 0

        /* Always visible (empty or with chips); shrinks after the betting timer */
        return (
            <Bets
                status={status}
                stake={stake}
                setStake={data => this.setStake(data)}
                selected={selected}
                chips={chips}
                list={list}
                setList={list => this.setState({ list })}
                balance={balance}
                max={max}
                notify={this.notify}
                result={result}
                sideResults={sideResults}
                doubleMain={doubleMain}
                mainStake={stakeInfo.main}
                insuranceOn={insuranceOn}
                insuranceWin={insuranceWin}
            />
        )
    }

    _chips = () => {

        const { status, stake, last, selected, list, balance } = this.state
        const { chips, max } = utils.getData(token)

        if (status !== CHOICE) return null

        return (
            <Chips
                chips={chips}
                stake={stake}
                setStake={data => this.setStake(data)}
                last={last}
                selected={selected}
                setSelected={selected => this.setState({ selected })}
                list={list}
                setList={list => this.setState({ list })}
                balance={balance}
                notify={this.notify}
                max={max}
            />
        )
    }

    _result = () => {
        const { status, result } = this.state
        const { currency } = utils.getData(token)
        return <Result status={status} result={result} currency={currency} />
    }

    _payout = () => {
        const { max, min, currency } = utils.getData(token)
        return <GamePayout currency={currency} max={max} min={min} />
    }

    _backgroundProps = () => {
        const { dealerInfo, status, transactions, gameInfo } = this.state
        const { currency, identifier, backColor, gameImg } = utils.getData(token)
        return {
	    token,
            gamePlace: env.gamePlace,
            currency,
            status,
            dealer: dealerInfo,
            sendMessage: message => this.sendMessage(message),
            sendSupportMessage: message => this.sendSupportMessage(message),
            info: gameInfo,
            transactions,
            gamePayout: this._payout(),
            identifier,
            rules: list,
            videoBackImg: gameImg,
            textileColor: backColor,
            historyComponent: History,
        }
    }

    render = () => {

        const { status, stake, newDevice, closed, isDisconnected, gameInfo, operations, dealerInfo, balance, lastWin, stakeInfo } = this.state
        const { max, min, currency, gameImg } = utils.getData(token)
        /* While betting -> live sum of chips being placed. Once the round is
           committed -> the server's committed total (reflects double / split). */
        const total = status === CHOICE ? utils.getTotal(stake) : (stakeInfo.total > 0 ? stakeInfo.total : utils.getTotal(stake))

        if (status === ERROR || newDevice || closed || isDisconnected) {
            return <Error newDevice={newDevice} closed={closed} isDisconnected={isDisconnected} />
        }

        if (status === LOADING) {
            return <Preloader url={gameImg} />
        }

        return (
            <Background {...this._backgroundProps()}>

                <div className={`bj-game status-${status}`}>

                    {this._timer()}

                    <div className="bj-card-box">
                        {this._dealer()}
                        {this._player()}
                    </div>

                    {this._controls()}
                    {this._bets()}
                    {this._chips()}
                    {this._result()}

                </div>

                <Suspense fallback={<div />}>
                    <GameInfo currency={currency} info={gameInfo} operations={operations} />
                    <GameControl title="ONE BLACKJACK" max={max} min={min} currency={currency} info={gameInfo} data={dealerInfo} gamePayout={this._payout()} onQuitGame={() => this.quitGame()} />
                    <Balance balance={balance} total={total} lastWin={lastWin} currency={currency} />
                    <FloatingMessages ref={ref => this._floatingMessages = ref} />
                    <Notification nextGame={status === WAITING} ref={ref => this._notify = ref} currency={currency} max={max} />
                    <Internet url={env.internetSpeedImgLink} setInternetSpeed={data => this.checkInternetSpeed(data)} />
                </Suspense>

            </Background>
        )
    }
}

export default App
